import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from "react";
import { Input, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";
const CompsTags = ({ tags = [], onChange, onAddTagRequest, onRemoveTagRequest, placeholder = "输入标签后按回车添加，支持用逗号分隔多个标签", disabled = false, maxTags, style, className, hideInput = false, draggable = true, }) => {
    // 输入框的值
    const [inputValue, setInputValue] = useState("");
    // 标签列表
    const [tagList, setTagList] = useState([]);
    // 当前悬停的标签 key
    const [hoveredTagKey, setHoveredTagKey] = useState(null);
    const [draggingKey, setDraggingKey] = useState(null);
    // 解析标签数据，支持数组或JSON字符串
    const parseTags = (tagsData) => {
        if (typeof tagsData === "string") {
            try {
                const parsed = JSON.parse(tagsData);
                return Array.isArray(parsed) ? parsed : [];
            }
            catch {
                return [];
            }
        }
        return Array.isArray(tagsData) ? tagsData : [];
    };
    // 初始化标签列表
    useEffect(() => {
        const parsedTags = parseTags(tags);
        setTagList(parsedTags);
    }, [tags]);
    // 生成标签唯一ID
    const generateTagId = () => {
        return `tag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };
    // 添加标签
    const handleAddTag = async () => {
        const trimmedValue = inputValue.trim();
        // 检查输入是否为空
        if (!trimmedValue) {
            return;
        }
        // 使用中英文逗号分隔输入值，支持多个标签
        const tagLabels = trimmedValue
            .split(/[,，]/) // 支持英文逗号和中文逗号
            .map((label) => label.trim()) // 去除每个标签的前后空格
            .filter((label) => label.length > 0); // 过滤掉空字符串
        // 如果没有有效的标签，直接返回
        if (tagLabels.length === 0) {
            setInputValue("");
            return;
        }
        // 过滤掉已存在的标签（不区分大小写）
        const existingLabels = new Set(tagList.map((tag) => tag.label.toLowerCase()));
        const newLabels = tagLabels.filter((label) => !existingLabels.has(label.toLowerCase()));
        // 如果没有新标签需要添加，清空输入框后返回
        if (newLabels.length === 0) {
            setInputValue("");
            return;
        }
        // 检查是否超过最大标签数量限制
        const currentTagCount = tagList.length;
        const remainingSlots = maxTags ? maxTags - currentTagCount : Infinity;
        // 如果剩余槽位不足，只添加能添加的标签
        const labelsToAdd = remainingSlots < newLabels.length
            ? newLabels.slice(0, remainingSlots)
            : newLabels;
        // 创建新标签数组
        const newTags = labelsToAdd.map((label) => ({
            id: generateTagId(),
            label: label,
        }));
        // 计算更新后的标签列表
        const updatedTags = [...tagList, ...newTags];
        // 如果有 onAddTagRequest 回调，先请求外部确认
        if (onAddTagRequest) {
            try {
                const allowed = await onAddTagRequest(newTags, updatedTags);
                // 如果外部拒绝，不更新标签列表，但清空输入框
                if (!allowed) {
                    setInputValue("");
                    return;
                }
            }
            catch (error) {
                // 如果请求失败，不更新标签列表，但清空输入框
                setInputValue("");
                return;
            }
        }
        // 更新标签列表
        setTagList(updatedTags);
        // 清空输入框
        setInputValue("");
        // 触发变化回调（仅在确认后触发）
        if (onChange) {
            onChange(updatedTags);
        }
    };
    // 删除标签
    const handleRemoveTag = async (tagToRemove) => {
        // 计算删除后的标签列表
        const updatedTags = tagList.filter((tag) => {
            // 如果有id，使用id比较，否则使用label比较
            if (tagToRemove.id !== undefined && tag.id !== undefined) {
                return tag.id !== tagToRemove.id;
            }
            return tag.label !== tagToRemove.label;
        });
        // 如果有 onRemoveTagRequest 回调，先请求外部确认
        if (onRemoveTagRequest) {
            try {
                const allowed = await onRemoveTagRequest(tagToRemove, updatedTags);
                // 如果外部拒绝，不删除标签
                if (!allowed) {
                    return;
                }
            }
            catch (error) {
                // 如果请求失败，不删除标签
                return;
            }
        }
        // 更新标签列表
        setTagList(updatedTags);
        // 触发变化回调（仅在确认后触发）
        if (onChange) {
            onChange(updatedTags);
        }
    };
    // 处理输入框回车事件
    const handleInputPressEnter = () => {
        handleAddTag();
    };
    // 拖拽排序：移动标签到目标位置
    const moveTag = (fromKey, toKey) => {
        if (fromKey === toKey)
            return;
        const fromIndex = tagList.findIndex((t) => (t.id || t.label) === fromKey);
        const toIndex = tagList.findIndex((t) => (t.id || t.label) === toKey);
        if (fromIndex < 0 || toIndex < 0)
            return;
        const next = [...tagList];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        setTagList(next);
        if (onChange)
            onChange(next);
    };
    return (_jsxs("div", { className: className, style: {
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
            ...style,
        }, children: [!hideInput && (_jsx("div", { style: { marginBottom: 4, flexShrink: 0 }, children: _jsx(Input, { value: inputValue, onChange: (e) => setInputValue(e.target.value), onPressEnter: handleInputPressEnter, placeholder: placeholder, disabled: disabled, allowClear: true, style: { fontSize: 14 } }) })), _jsx("div", { style: {
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    minHeight: 0,
                    width: "100%",
                    paddingTop: 16,
                    paddingBottom: 16,
                    boxSizing: "border-box",
                }, children: _jsx("div", { style: {
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        alignContent: "flex-start",
                        alignItems: "flex-start",
                        width: "100%",
                        minHeight: "min-content",
                        boxSizing: "border-box",
                    }, children: tagList.length === 0 ? (_jsx("div", { style: {
                            width: "100%",
                            textAlign: "center",
                            color: "#999",
                            padding: "20px 0",
                        }, children: "\u6682\u65E0\u6807\u7B7E" })) : (tagList.map((tag) => {
                        // 获取标签的唯一 key
                        const tagKey = tag.id || tag.label;
                        // 判断当前标签是否被悬停
                        const isHovered = hoveredTagKey === tagKey;
                        const canDrag = draggable && !disabled;
                        return (_jsx(Tag, { color: tag.color, closable: !disabled, onClose: () => handleRemoveTag(tag), draggable: canDrag, onDragStart: () => setDraggingKey(tagKey), onDragEnd: () => setDraggingKey(null), onDragOver: (e) => {
                                if (!canDrag)
                                    return;
                                e.preventDefault();
                            }, onDrop: (e) => {
                                if (!canDrag)
                                    return;
                                e.preventDefault();
                                if (draggingKey == null)
                                    return;
                                moveTag(draggingKey, tagKey);
                            }, closeIcon: _jsx(CloseOutlined, { style: {
                                    verticalAlign: "middle",
                                    marginTop: "-2px",
                                    marginRight: "-3px",
                                } }), style: {
                                fontSize: 12,
                                margin: 0,
                                boxShadow: isHovered
                                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                                    : "none",
                                transition: "box-shadow 0.2s ease",
                                cursor: canDrag ? "move" : "default",
                                opacity: draggingKey === tagKey ? 0.7 : 1,
                            }, onMouseEnter: () => setHoveredTagKey(tagKey), onMouseLeave: () => setHoveredTagKey(null), children: tag.label }, tagKey));
                    })) }) })] }));
};
export default CompsTags;
